import { useState } from "react";
import dayjs from "dayjs";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { useMarkBonificoOrderAsPaid } from "../../ProductService/BonificoOrder/useMarkBonificoOrderAsPaid.ts";
import { useLastInvoicedValueDate } from "../../ProductService/BonificoOrder/useLastInvoicedValueDate.ts";
import { useBonificoOrderInvoicing } from "../../ProductService/BonificoOrder/useBonificoOrderInvoicing.ts";
import {
  b2bAmountPaidInCents,
  b2bRemainingAmountInCents,
  BonificoOrderDTO,
  BonificoOrderStatus,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { issuesInvoiceOnPayment } from "../../ProductService/BonificoOrder/bonificoBilling.ts";
import {
  isArticle7Ter,
  vatExemptionFor,
} from "../../ProductService/BonificoOrder/article7Ter.ts";
import { BonificoBillingSummary } from "./BonificoBillingSummary.tsx";
import { Article7TerSwitch } from "./Article7TerSwitch.tsx";
import { EditBonificoBillingModal } from "./EditBonificoBillingModal.tsx";
import { OverlayDialog } from "../../UI/Modal/OverlayDialog.tsx";

const VALUE_DATE_ONLY_NOTICE =
  "Viene salvata solo la data valuta, senza emettere la fattura né inserire transazioni su BigQuery. Puoi modificarla finché non emetti la fattura con «Emetti fattura».";

const DRAFT_VALUE_DATE_NOTICE =
  "Viene salvata solo la data valuta sulla bozza: quando la confermi l'ordine passa direttamente in «Fattura da emettere». Puoi modificarla o rimuoverla fino all'emissione della fattura.";

function formatEuros(amountInCents: number): string {
  return `€${(amountInCents / 100).toFixed(2)}`;
}

type Props = {
  order: BonificoOrderDTO;
  onSuccess: () => void;
  onClose: () => void;
  onOrderUpdated?: (order: BonificoOrderDTO) => void;
};

export function MarkAsPaidModal({
  order: initialOrder,
  onSuccess,
  onClose,
  onOrderUpdated,
}: Props) {
  const [order, setOrder] = useState(initialOrder);
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const contract = order.b2bContract ?? undefined;
  const remainingAmountInCents = contract
    ? b2bRemainingAmountInCents(contract)
    : 0;
  const savesOnlyValueDate = issuesInvoiceOnPayment(order);
  const isChangingValueDate = savesOnlyValueDate && !!order.valueDateUtc;
  const isDraft = order.status === BonificoOrderStatus.Draft;
  const [valueDate, setValueDate] = useState(
    order.valueDateUtc ? dayjs(order.valueDateUtc).format("YYYY-MM-DD") : "",
  );
  const [amountInEuros, setAmountInEuros] = useState(
    contract ? (remainingAmountInCents / 100).toFixed(2) : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [isArticle7TerApplied, setIsArticle7TerApplied] = useState(
    isArticle7Ter(initialOrder.vatExemption),
  );
  const { markAsPaid, loading: markingAsPaid } = useMarkBonificoOrderAsPaid();
  const { setValueDate: saveValueDate, loading: savingValueDate } =
    useBonificoOrderInvoicing();
  const loading = markingAsPaid || savingValueDate;
  const { lastValueDateUtc } = useLastInvoicedValueDate();

  const validateAmountInCents = (): number | string | undefined => {
    if (!contract) return undefined;
    const amountInCents = Math.round(Number(amountInEuros) * 100);
    if (!Number.isFinite(amountInCents) || amountInCents <= 0)
      return "Inserisci l'importo ricevuto";
    if (amountInCents > remainingAmountInCents)
      return `L'importo non può superare quanto resta da pagare (${formatEuros(remainingAmountInCents)})`;
    return amountInCents;
  };

  const handleConfirm = async () => {
    if (!valueDate) {
      setError("Inserisci la data di valuta");
      return;
    }
    const amountInCents = validateAmountInCents();
    if (typeof amountInCents === "string") {
      setError(amountInCents);
      return;
    }
    setError(null);
    try {
      if (savesOnlyValueDate)
        await saveValueDate(
          order.orderId,
          valueDate,
          vatExemptionFor(isArticle7TerApplied),
        );
      else await markAsPaid(order.orderId, valueDate, amountInCents);
      onSuccess();
    } catch (e) {
      setError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante il salvataggio. Riprova.",
        ),
      );
    }
  };

  return (
    <OverlayDialog
      onClose={onClose}
      isClosable={!loading}
      panelClassName="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4"
    >
      <h2 className="text-xl font-bold">
        {contract
          ? "Registra pagamento"
          : isChangingValueDate
            ? "Modifica data valuta"
            : "Segna come pagato"}
      </h2>
      <p className="text-sm text-gray-600">
        Ordine: <span className="font-mono text-xs">{order.orderId}</span>
        <br />
        Cliente: <strong>{order.customer.email}</strong>
      </p>
      <BonificoBillingSummary
        order={order}
        onEdit={() => setIsEditingBilling(true)}
      />
      {savesOnlyValueDate && (
        <Article7TerSwitch
          checked={isArticle7TerApplied}
          onChange={setIsArticle7TerApplied}
        />
      )}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
        {contract
          ? contract.payments.length === 0
            ? "Verrà inserita solo la transazione su BigQuery, senza fattura: al primo pagamento si aggiunge anche la riga del contratto alla data del contratto."
            : "Verrà inserita solo la transazione su BigQuery alla data di valuta, senza fattura."
          : savesOnlyValueDate
            ? isDraft
              ? DRAFT_VALUE_DATE_NOTICE
              : VALUE_DATE_ONLY_NOTICE
            : "La fattura di questo ordine è stata emessa alla creazione: verrà inserita solo la transazione su BigQuery alla data di valuta."}
      </div>
      {contract && (
        <div className="flex flex-col gap-1">
          <p className="text-sm text-gray-600">
            Contratto {formatEuros(contract.contractAmountInCents.amount)} · già
            pagati {formatEuros(b2bAmountPaidInCents(contract))} · restano{" "}
            <strong>{formatEuros(remainingAmountInCents)}</strong>
          </p>
          <label className="text-sm font-medium text-gray-700">
            Importo ricevuto (€)
          </label>
          <TextInput
            type="number"
            step="0.01"
            min="0"
            value={amountInEuros}
            onChange={(e) => setAmountInEuros(e.target.value)}
          />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">
          Data di valuta
        </label>
        <TextInput
          type="date"
          value={valueDate}
          max={dayjs().format("YYYY-MM-DD")}
          onChange={(e) => setValueDate(e.target.value)}
        />
        {savesOnlyValueDate && lastValueDateUtc && (
          <p className="text-xs text-gray-500">
            Ultima fattura emessa con data valuta{" "}
            {dayjs(lastValueDateUtc).format("DD/MM/YYYY")}: la data non può
            essere precedente.
          </p>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
        >
          Annulla
        </button>
        <PrimaryButton
          label={
            loading
              ? "Salvataggio..."
              : contract
                ? "Registra pagamento"
                : savesOnlyValueDate
                  ? "Salva data valuta"
                  : "Conferma pagamento"
          }
          onClick={handleConfirm}
          disabled={loading}
        />
      </div>
      {isEditingBilling && (
        <EditBonificoBillingModal
          order={order}
          onSaved={(updated) => {
            setOrder(updated);
            setIsArticle7TerApplied(isArticle7Ter(updated.vatExemption));
            setIsEditingBilling(false);
            onOrderUpdated?.(updated);
          }}
          onClose={() => setIsEditingBilling(false)}
        />
      )}
    </OverlayDialog>
  );
}
